import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { LanguageSwitcher } from './LanguageSwitcher'

/**
 * Подвал EdPad Учитель — стиль и структура по образцу edpadclass.ru.
 * 4 колонки: О компании / Продукты / Правовые документы / Контакты.
 * Тёмный фон, тонкие линии, фирменные акценты #a78bfa.
 */
export function PublicFooter() {
  const { t } = useTranslation()
  const year = new Date().getFullYear()
  return (
    <footer className="bg-[#0f0a1c] text-gray-300 mt-auto">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 md:py-14">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-10">
          {/* Логотип + краткое описание */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-3 group">
              <img src="/edpad-logo.png" alt="EdPad" className="w-9 h-9 rounded transition-transform group-hover:scale-105" />
              <span className="font-semibold text-white text-lg">{t('footer.brand')}</span>
            </Link>
            <p className="text-sm text-gray-400 leading-relaxed mb-4">
              {t('footer.tagline')}
            </p>
            <div className="text-xs text-gray-500 leading-relaxed mb-4">
              ООО «Смарт Лайф»<br />
              ОГРН: 1185029013927<br />
              ИНН: 5047212715
            </div>
            <LanguageSwitcher className="text-sm border border-white/15 rounded-md px-2 py-1.5 bg-white/5 text-gray-200 cursor-pointer max-w-[180px]" />
          </div>

          {/* Продукт */}
          <FooterColumn title={t('footer.productTitle')}>
            <FooterLink href="#features">{t('footer.features')}</FooterLink>
            <FooterLink href="#how">{t('footer.how')}</FooterLink>
            <FooterLink href="#pricing">{t('footer.pricing')}</FooterLink>
            <FooterLink href="#faq">{t('footer.faq')}</FooterLink>
            <FooterLink to="/login">{t('footer.loginRegister')}</FooterLink>
          </FooterColumn>

          {/* Правовые документы */}
          <FooterColumn title={t('footer.legalTitle')}>
            <FooterLink to="/privacy">{t('footer.privacy')}</FooterLink>
            <FooterLink to="/consent">{t('footer.consent')}</FooterLink>
            <FooterLink to="/about">{t('footer.about')}</FooterLink>
            <li className="text-gray-500 text-sm">{t('footer.compliant')}</li>
          </FooterColumn>

          {/* Экосистема + Контакты */}
          <FooterColumn title={t('footer.ecosystemTitle')}>
            <FooterLink external href="https://edpadclass.ru">
              {t('footer.edpadClass')}
            </FooterLink>
            <FooterLink external href="https://smart-life.pro">
              {t('footer.edpadTablet')}
            </FooterLink>
            <li className="pt-3 mt-2 border-t border-white/5">
              <a
                href="mailto:info@smart-life.pro"
                className="inline-flex items-center gap-1.5 text-sm text-purple-300 hover:text-purple-200 transition-colors"
              >
                ✉️ info@smart-life.pro
              </a>
            </li>
          </FooterColumn>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-gray-500">
          <div>{t('footer.rights', { year })}</div>
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
            <span>{t('footer.hosting')}</span>
            <span className="hidden md:inline">·</span>
            <span>{t('footer.address')}</span>
          </div>
        </div>
      </div>
    </footer>
  )
}

function FooterColumn({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="text-[11px] font-bold text-white/90 uppercase tracking-widest mb-4">
        {title}
      </h4>
      <ul className="space-y-2.5">{children}</ul>
    </div>
  )
}

function FooterLink({
  children,
  to,
  href,
  external,
}: {
  children: React.ReactNode
  to?: string
  href?: string
  external?: boolean
}) {
  const cls = 'text-sm text-gray-400 hover:text-white transition-colors'
  if (to) {
    return (
      <li>
        <Link to={to} className={cls}>
          {children}
        </Link>
      </li>
    )
  }
  return (
    <li>
      <a
        href={href}
        target={external ? '_blank' : undefined}
        rel={external ? 'noreferrer' : undefined}
        className={cls}
      >
        {children}
      </a>
    </li>
  )
}
